import React, { useState, useEffect, useRef, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import CustomPagination from "../CustomPagination/CustomPagination";
import useKeyScroll from "../../Hooks/useKeyScroll";

const TruncatedCell = ({ text, id, maxLines = 2 }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollHeight > textRef.current.clientHeight
        );
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [text]);

  const content = (
    <div
      ref={textRef}
      className="gallery_list_file_name w-100 m-0 p-0"
      style={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        cursor: isTruncated ? "pointer" : "default",
      }}
    >
      {text}
    </div>
  );

  return isTruncated ? (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};

export default function ListView({
  setSelectedPhoto,
  updateSelectedCheckboxes,
  images,
  checkedState,
  onPhotoClick,
  onSelectionCountChange,
  BASE_URL,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalRecords,
  totalPages,
  sortConfig,
  setSortConfig,
}) {
  const scrollContainerRef = useKeyScroll();

  const handleCheckboxClick = (item) => {
    const clickedGalleryId = item.galleryId;

    const updatedCheckedState = checkedState.map((checked, i) =>
      images[i].galleryId === clickedGalleryId ? !checked : false
    );
    updateSelectedCheckboxes(updatedCheckedState);

    const selectedImages = images.filter((_, i) => updatedCheckedState[i]);
    onSelectionCountChange(selectedImages.length);

    if (selectedImages.length === 1) {
      setSelectedPhoto(selectedImages[0].imgUrl || selectedImages[0].documentUrl);
      onPhotoClick(selectedImages[0].galleryId);
    } else if (selectedImages.length > 1) {
      setSelectedPhoto(selectedImages[0].imgUrl || selectedImages[0].documentUrl);
    } else {
      setSelectedPhoto("");
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const currentSortedImages = Array.isArray(images) ? images : [];

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const normalized =
      typeof dateString === "string" ? dateString.replace(" ", "T") : dateString;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return dateString;

    const dateFormatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
  };

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "ascending" ? faArrowUp : faArrowDown}
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const handleSortChange = (selectedColumn) => {
    setSortConfig((prev) => ({
      key: selectedColumn.sortField,
      direction:
        prev.key === selectedColumn.sortField &&
        prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            IMAGE NAME {renderSortIcon("name")}
          </div>
        ),
        selector: (row) => row.name,
        sortable: true,
        sortField: "name",
        grow: 2,
        minWidth: "280px",
        cell: (row, index) => {
          const originalIndex = images.findIndex(
            (img) => img.galleryId === row.galleryId
          );
          return (
            <div
              className="gallery_list_name_cell"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleCheckboxClick(row);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCheckboxClick(row);
                }
              }}
            >
              <input
                type="checkbox"
                className="gallery_list_row_checkbox"
                checked={Boolean(checkedState[originalIndex])}
                readOnly
                tabIndex={-1}
                aria-label={`Select ${row.name}`}
              />
              <TruncatedCell
                text={row.name || "N/A"}
                id={`tooltip-gallery-name-${index}`}
                maxLines={2}
              />
            </div>
          );
        },
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            DATE UPLOADED {renderSortIcon("createdOn")}
          </div>
        ),
        selector: (row) => row.createdOn,
        sortable: true,
        sortField: "createdOn",
        width: "220px",
        cell: (row) => (
          <span
            className="gallery_list_td_date gallery_list_row_clickable"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleCheckboxClick(row);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCheckboxClick(row);
              }
            }}
          >
            {formatDateTime(row.createdOn)}
          </span>
        ),
      },
    ],
    [sortConfig, checkedState, images]
  );

  const customStyles = {
    table: {
      style: {
        minWidth: "max-content",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        minHeight: "52px",
        minWidth: "max-content",
      },
    },
    headCells: {
      style: {
        fontSize: "13px",
        textTransform: "uppercase",
        fontWeight: "700",
        color: "#444444",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    cells: {
      style: {
        color: "#6c757d",
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "normal",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        minWidth: "max-content",
        cursor: "pointer",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f5f6ff",
        outline: "none",
        transition: "background-color 0.2s ease",
      },
    },
  };

  useEffect(() => {
    if (images.length > 0 && checkedState.every((checked) => !checked)) {
      const firstImage = images[0];
      const updatedCheckedState = checkedState.map((_, i) => i === 0);

      updateSelectedCheckboxes(updatedCheckedState);
      setSelectedPhoto(firstImage.imgUrl || firstImage.documentUrl);
      onPhotoClick(firstImage.galleryId);
      onSelectionCountChange(1);
    }
  }, [images]);

  return (
    <div className="gallery_list_view_root">
      <div className="gallery_list_card">
        <div
          className="gallery_list_table_wrap"
          ref={scrollContainerRef}
          tabIndex="0"
          style={{ outline: "none" }}
        >
          {currentSortedImages.length === 0 ? (
            <div className="gallery-list-empty-state">NO IMAGE AVAILABLE</div>
          ) : (
            <DataTable
              columns={columns}
              data={currentSortedImages}
              customStyles={customStyles}
              sortServer
              onSort={handleSortChange}
              sortIcon={<></>}
              highlightOnHover
              pointerOnHover
              dense={false}
              onRowClicked={(row) => handleCheckboxClick(row)}
              conditionalRowStyles={[
                {
                  when: (row) => {
                    const idx = images.findIndex(
                      (img) => img.galleryId === row.galleryId
                    );
                    return checkedState[idx];
                  },
                  style: {
                    backgroundColor: "#f5f6ff",
                  },
                },
              ]}
              className="gallery-list-table-react"
            />
          )}
        </div>
        <div className="gallery-list-footer-pagination">
          <CustomPagination
            totalItems={totalRecords}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>
    </div>
  );
}
